import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	SimpleChanges,
	EventEmitter,
	Component,
	OnChanges,
	Output,
	Input,
} from '@angular/core';
import {
	NotificationFormControlIds,
	ISubscriptionControl,
	IInputTimeOptions,
	INotificationsSet,
} from '@modules/notification-management/interfaces/notifications.interface';
import { conditionalLimit, duplicateName, emptyEmailList } from '@modules/notification-management/validators';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { CheckboxStates } from '@modules/notification-management/notification.interface';
import { MatCheckboxChange } from '@angular/material/checkbox/checkbox';
import { MatChipInputEvent } from '@angular/material/chips';
import { clonedeep } from '@utils/helpers';
import { share, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';

// parent form toggle control
const CHECKED = 'checked';

@Component({
	selector: 'app-notification-set-form-new',
	templateUrl: './notification-set-form-new.component.html',
	styleUrls: ['./notification-set-form-new.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	exportAs: 'notificationSetForm',
})
export class NotificationSetFormNewComponent implements OnChanges {
	@Input()
	public set slackChannels(channels$: Observable<string[]>) {
		this._isLoading = true;
		this._slackChannels$ = channels$.pipe(
			share(),
			tap((channels) => {
				this._isLoading = false;
				if (!!this.editNotification && !!this.editNotification.targets.slack && channels.length) {
					this.formSlackChannels.patchValue(this.editNotification.targets.slack.channels);
				}
				this.cdr.markForCheck();
			}),
		);
	}

	@Input() isUpdating = false;
	@Input() existingSetNames: string[] = [];
	@Input() editNotification?: INotificationsSet;
	@Input() hasSlack?: boolean;
	@Input() hasTeams?: boolean;
	@Output() formData: EventEmitter<any> = new EventEmitter<any>();

	public form: FormGroup = this.initForm();
	private readonly CHECKBOX_STATES: typeof CheckboxStates = CheckboxStates;
	public readonly FORM_CONTROL_IDS = NotificationFormControlIds;
	public readonly THRESHOLD_CONFIG = {
		zdFSLow: 0,
		zdFSHigh: '10,000,000,000',
		monitoredFSSLow: 0,
		monitoredFSHigh: 100,
	};

	private _slackChannels$: Observable<string[]>;
	private _isLoading: boolean;

	constructor(private cdr: ChangeDetectorRef) {}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.editNotification?.currentValue?.name) {
			this.form.patchValue(this.preparePatchValue(this.editNotification));
			this.formNameControl.disable({ onlySelf: true });
			if (!!this.editNotification.targets.email) {
				this.fillFormArray(
					this.notificationSetFormEmails,
					this.setFormArray(this.editNotification.targets.email.emails || []),
				);
			}
		}
		if (changes.existingSetNames?.currentValue?.length) {
			this.formNameControl.setValidators([Validators.required, duplicateName(this.existingSetNames)]);
			this.formNameControl.updateValueAndValidity();
		}
		if (changes.hasSlack?.currentValue === false) {
			this.form.get('targets.slack').disable({ onlySelf: true });
		}
		if (changes.hasTeams?.currentValue === false) {
			this.form.get('targets.teams').disable({ onlySelf: true });
		}
	}

	public generateInputTimeOptions(): IInputTimeOptions[] {
		const timeOptions = [];
		for (let i = 5; i <= 60; i = i + 5) {
			timeOptions.push({
				value: i,
				display: i.toString(),
			});
		}

		return timeOptions;
	}

	public get slackChannels$(): Observable<string[]> {
		return this._slackChannels$;
	}

	public get isLoading(): boolean {
		return this._isLoading;
	}

	public get formNameControl(): AbstractControl {
		return this.form.get('name');
	}

	public get slackSubscribed() {
		return this.form.get('targets.slack.subscribed').value;
	}

	public get formSlackChannels(): FormControl {
		return this.form.get('targets.slack.channels') as FormControl;
	}

	public get notificationSetFormEmails(): FormArray {
		return this.form.get('targets.email.emails') as FormArray;
	}

	public get emailErrors(): any {
		return (
			this.notificationSetFormEmails.getError('incorrectEmail') ||
			this.notificationSetFormEmails.getError('duplicateEmail')
		);
	}

	private fillFormArray(control: FormArray, data: AbstractControl[]): FormArray {
		if (!data.length) {
			return;
		}
		data.forEach((item) => {
			control.push(item);
		});

		return control;
	}

	private preparePatchValue(data: INotificationsSet) {
		return {
			name: data.name,
			active: data.active,
			org_id: data.org_id,
			subscriptions: {
				zesty_disk: {
					critical: {
						checked: Object.keys(data.subscriptions.zesty_disk.critical).length,
						...data.subscriptions.zesty_disk.critical,
					},
					info: {
						checked: Object.keys(data.subscriptions.zesty_disk.info).length,
						...data.subscriptions.zesty_disk.info,
					},
				},
				unused_resources: {
					info: {
						checked: Object.keys(data.subscriptions.unused_resources.info).length,
						...data.subscriptions.unused_resources.info,
					},
				},
			},
			targets: {
				teams: {
					subscribed: !!data.targets.teams,
				},
				slack: {
					...this.formSlackChannels,
					subscribed: !!data.targets.slack,
				},
				email: {
					...this.notificationSetFormEmails,
					subscribed: !!data.targets.email,
				},
			},
		};
	}

	private initForm(): FormGroup {
		return new FormGroup({
			name: new FormControl('', Validators.required),
			subscriptions: new FormGroup(
				{
					zesty_disk: new FormGroup({
						critical: new FormGroup({
							checked: new FormControl(false),
							zesty_disk_resize_failures: new FormGroup({
								subscribed: new FormControl(false),
							}),
							zesty_disk_agent_stop_reporting: new FormGroup({
								subscribed: new FormControl(false),
							}),
						}),
						info: new FormGroup({
							checked: new FormControl(false),
							monitored_fs_utilization: new FormGroup({
								subscribed: new FormControl(false),
								threshold: new FormControl(0, [conditionalLimit('min', 0), conditionalLimit('max', 100)]),
								snooze_hours: new FormControl(0, [conditionalLimit('min', 0), conditionalLimit('max', 744)]),
							}),
							zesty_disk_fs_size: new FormGroup({
								subscribed: new FormControl(false),
								threshold: new FormControl(0, [conditionalLimit('min', 0), conditionalLimit('max', 10000000000)]),
								snooze_hours: new FormControl(0, [conditionalLimit('min', 0), conditionalLimit('max', 744)]),
							}),
						}),
					}),
					unused_resources: new FormGroup({
						info: new FormGroup({
							checked: new FormControl(false),
							unused_resources_policy_update: new FormGroup({
								subscribed: new FormControl(false),
							}),
							unused_resources_actions: new FormGroup({
								subscribed: new FormControl(false),
							}),
						}),
					}),
				},
				this.atLeastOneTopicValidator,
			),
			targets: new FormGroup(
				{
					teams: new FormGroup({
						subscribed: new FormControl(false),
					}),
					slack: new FormGroup({
						subscribed: new FormControl(false),
						channels: new FormControl([]),
					}),
					email: new FormGroup(
						{
							subscribed: new FormControl(false),
							emails: new FormArray([]),
						},
						[emptyEmailList()],
					),
				},
				this.atLeastOneTargetValidator,
			),
		});
	}

	private setFormArray(arr: string[]): AbstractControl[] {
		if (!arr.length) {
			return [];
		}
		return arr.map((i) => new FormControl(i));
	}

	public descendantsPartiallySelected(id: string): boolean {
		return this.checkFormStatus(id) === this.CHECKBOX_STATES.partial;
	}

	private checkFormStatus(id: string): string {
		const formItemPath = id.split('-');
		const fullFormItemPath = 'subscriptions.' + formItemPath.join('.');
		const formGroup = this.form.get(fullFormItemPath);
		const formGroupValue = formGroup.value;
		const controlsLen = Object.values(formGroupValue).length - 1;
		const trueLen = Object.values(formGroupValue).filter((i: ISubscriptionControl) => i?.subscribed).length;

		if (trueLen > 0 && trueLen !== controlsLen) {
			return this.CHECKBOX_STATES.partial;
		}

		if (trueLen === 0) {
			return this.CHECKBOX_STATES.empty;
		}

		return this.CHECKBOX_STATES.full;
	}

	public handleCheckSubForms(e: MatCheckboxChange, id: string): void {
		const fullFormItemPath = this.getFormControlPath(id);
		const formGroup = this.form.get(fullFormItemPath)['controls'];
		for (const i in formGroup) {
			if (i !== CHECKED) {
				formGroup[i].get('subscribed').setValue(e.checked);
				Object.values(formGroup[i].controls).forEach(this.updateValidity);
			}
		}
	}

	private getFormControlPath(id: string): string {
		return 'subscriptions.' + id;
	}

	private updateValidity(control: AbstractControl): void {
		control.updateValueAndValidity();
	}

	public toggleParent({ checked }: MatCheckboxChange, id: string, controlName?: string): void {
		const formGroup = this.getFormGroup(id);
		const isPartialSubForm = this.checkFormStatus(id);

		switch (isPartialSubForm) {
			case this.CHECKBOX_STATES.full:
				formGroup.setValue(true);
				break;
			case this.CHECKBOX_STATES.empty:
				formGroup.setValue(false);
				break;
		}

		if (controlName) {
			Object.values((formGroup.parent.get(controlName) as FormGroup).controls).forEach(this.updateValidity);
		}
	}

	private getFormGroup(id: string): AbstractControl {
		const path = this.getFormControlPath(id);

		return this.form.get(path + '.' + CHECKED);
	}

	public getFormError(id: string): AbstractControl {
		return this.form.get(id);
	}

	public removeEmail(index: number): void {
		this.notificationSetFormEmails.removeAt(index);
	}

	public addEmail(event: MatChipInputEvent): void {
		if (event.value) {
			if (this.validateEmail(event.value)) {
				if (this.notificationSetFormEmails.value.indexOf(event.value.toLowerCase()) < 0) {
					this.notificationSetFormEmails.push(new FormControl(event.value.toLowerCase()));
				} else {
					this.notificationSetFormEmails.setErrors({ duplicateEmail: true });
					return;
				}
			} else {
				this.notificationSetFormEmails.setErrors({ incorrectEmail: true });
				return;
			}
		}
		if (event.input) {
			event.input.value = '';
		}
	}

	// @Todo: rewrite to Angular email validator
	private validateEmail(email: string): boolean {
		const re =
			/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
		return re.test(String(email).toLowerCase());
	}

	public handleEmailChange(event: Event): void {
		if (this.notificationSetFormEmails.value.indexOf((event.target as HTMLInputElement).value.toLowerCase()) >= 0) {
			this.notificationSetFormEmails.setErrors({ duplicateEmail: true });
		} else {
			this.notificationSetFormEmails.setErrors({ duplicateEmail: null });
		}
	}

	public onSubmit(): void {
		if (this.form.invalid) {
			return;
		}
		const formValue = clonedeep(this.form.getRawValue());
		this.removeUnsubscribedValues(formValue);
		this.removeKeyInObject(formValue, CHECKED);
		this.removeEmptyKeys(formValue);

		this.formData.emit(formValue);
	}

	private removeEmptyKeys(obj): void {
		for (const prop in obj) {
			if (typeof obj[prop] === 'object') {
				if (!Object.keys(obj[prop]).length) {
					delete obj[prop];
				} else {
					this.removeEmptyKeys(obj[prop]);
				}
			}
		}
	}

	private removeUnsubscribedValues(obj): void {
		for (const prop in obj) {
			if (obj.hasOwnProperty(prop)) {
				if (obj.subscribed === false) {
					for (const innerProp in obj) {
						if (obj.hasOwnProperty(innerProp)) {
							delete obj[innerProp];
						}
					}
				} else if (typeof obj[prop] === 'object') {
					this.removeUnsubscribedValues(obj[prop]);
				}
			}
		}
	}

	private removeKeyInObject(obj, key: string): void {
		for (const prop in obj) {
			if (obj.hasOwnProperty(prop)) {
				if (prop === key) {
					delete obj[prop];
				} else if (typeof obj[prop] === 'object') {
					this.removeKeyInObject(obj[prop], key);
				}
			}
		}
	}

	private atLeastOneTargetValidator(group: FormGroup): { [key: string]: any } {
		let isAtLeastOne = false;
		if (group && group.controls) {
			for (const control in group.controls) {
				if (
					group.controls.hasOwnProperty(control) &&
					group.controls[control].valid &&
					group.controls[control].value.subscribed
				) {
					isAtLeastOne = true;
					break;
				}
			}
		}

		return isAtLeastOne ? null : { required: true };
	}

	private atLeastOneTopicValidator(group: FormGroup): { [key: string]: any } {
		let isAtLeastOne = false;
		if (group && group.controls) {
			for (const topicGroupControlName in group.controls) {
				if (group.controls.hasOwnProperty(topicGroupControlName)) {
					const topicGroupControl = group.controls[topicGroupControlName] as FormGroup; // f.e. zesty_disk
					for (const topicSeverityControlName in topicGroupControl.controls) {
						if (topicGroupControl.controls.hasOwnProperty(topicSeverityControlName)) {
							const topicSeverityControl = topicGroupControl.controls[topicSeverityControlName] as FormGroup; // f.e. critical
							for (const topicSeverityControlKey in topicSeverityControl.value) {
								if (
									topicSeverityControl.value.hasOwnProperty(topicSeverityControlKey) &&
									topicSeverityControlKey !== CHECKED &&
									topicSeverityControl.value[topicSeverityControlKey].subscribed
								) {
									isAtLeastOne = true;
									break;
								}
							}
						}
					}
				}
			}
		}

		return isAtLeastOne ? null : { required: true };
	}
}
